import { useRef, useState, useEffect, useMemo } from "react";
import axios from "axios";
import debounce from "lodash/debounce";
import { toastError } from "./toastHelper";

// Shared autosave lifecycle for the Beefree editor. Owns the whole concern so
// manual save and autosave can't race each other:
//
//   - `autosave(templateData)` — debounced; call it on every editor change.
//   - `isAutosaving` — drives UI (e.g. a "Saving…" state on the Save button).
//   - `cancel()` — drop any pending debounce AND supersede the in-flight save.
//     Call this at the START of a MANUAL save so a slower autosave carrying
//     older content can't land after (and overwrite) the manual write.
//   - a CancelToken supersedes the previous in-flight autosave so a slower,
//     older request can't overwrite newer content.
//   - the pending save is flushed on unmount (persist the last edit instead of
//     dropping it) and setState is guarded so it never touches a torn-down
//     component.
//
// `request(templateData, cancelToken)` MUST return the axios promise and stay
// SILENT: no success toast, and no setState that feeds the template back into
// the editor (a round-trip would stomp in-flight keystrokes). Guard on the
// record id inside `request` and return a resolved promise if there's nothing
// to save yet. Failures are surfaced here via a single toast.
export const useTemplateAutosave = (request, { wait = 1200, maxWait = 5000 } = {}) => {
  const [isAutosaving, setIsAutosaving] = useState(false);
  const cancelRef = useRef(null);
  const mountedRef = useRef(true);

  // Keep the latest request in a ref so the memoized debounce always calls the
  // current closure (fresh record id / template) without re-creating its timer.
  const requestRef = useRef(request);
  useEffect(() => { requestRef.current = request; }, [request]);

  const debouncedAutosave = useMemo(() => {
    const run = (templateData) => {
      if (cancelRef.current) cancelRef.current.cancel("superseded");
      const source = axios.CancelToken.source();
      cancelRef.current = source;

      if (mountedRef.current) setIsAutosaving(true);
      // Promise.resolve().then wrapper so a synchronous throw in `request` still
      // routes through .catch instead of escaping as an unhandled error.
      return Promise.resolve()
        .then(() => requestRef.current(templateData, source.token))
        .then(() => {
          if (mountedRef.current) setIsAutosaving(false);
        })
        .catch((error) => {
          // A cancelled request was superseded by a newer save (or a manual
          // save) that owns the state from here — leave the flag alone.
          if (axios.isCancel(error)) return;
          // eslint-disable-next-line no-console
          console.error("Autosave failed:", error);
          if (mountedRef.current) setIsAutosaving(false);
          toastError("Couldn’t autosave — click Save to keep your changes.");
        });
    };

    return debounce(run, wait, { maxWait });
  }, [wait, maxWait]);

  // Cancel a queued autosave and supersede any in-flight one. A manual save
  // calls this first, then issues its own write, so autosave can never land
  // after the manual save and revert it.
  const cancel = () => {
    debouncedAutosave.cancel();
    if (cancelRef.current) cancelRef.current.cancel("superseded-by-manual-save");
    // The aborted request's .catch early-returns on isCancel without clearing
    // the flag, so clear it here or the Save button stays stuck on "Saving…".
    if (mountedRef.current) setIsAutosaving(false);
  };

  // Flush (not cancel) on unmount so the last pending edit still persists; the
  // setState guard above keeps the fired request off the torn-down component.
  useEffect(
    () => () => {
      mountedRef.current = false;
      debouncedAutosave.flush();
    },
    [debouncedAutosave]
  );

  return { isAutosaving, autosave: debouncedAutosave, cancel };
};
